import { cardPayOnlineLabel, centsToEuros, formatCents, parseMoneyToCents, qrPayLabel, tt } from '@agos/shared'

import type { DayTotals } from './wallet'

/**
 * §9.6's takings calculator — the port of `TrzbaKalkulackaModal` (481 lines).
 *
 * A driver ends a shift holding cash and a list of things they spent it on. This works out what is left
 * to hand over, records it, and — the part legacy skips — says where each number came from.
 *
 * **The arithmetic is the server's, not this screen's.** `HandoverReconciliation.cashToHandOver`:
 *
 * ```java
 * totals.revenue()
 *     .subtract(totals.cardOnline())     // never reached the driver
 *     .subtract(totals.qrPay())          // §10A.390 - nor did a QR payment at the door
 *     .subtract(submitted.cardAmount())  // the terminal's own figure, not the orders'
 *     .subtract(submitted.vouchersAmount()) … .subtract(submitted.payout2Amount())
 * ```
 *
 * Two things follow, and both are why this is not a free-form form.
 *
 * 1. **The card deduction is typed, on purpose.** *"Card taken at a terminal is deducted using the figure
 *    read off the terminal itself, not the one the orders imply — those two disagree when a payment was
 *    taken outside the system, and the terminal is what the bank will settle."* So the field is prefilled
 *    from the orders as a **starting point that has to be checked**, and says so. Prefilling it silently
 *    would turn a deliberate cross-check into a rubber stamp; leaving it empty would make the driver add
 *    up two numbers the screen already knows.
 * 2. **The submitted totals are advisory.** The controller says the order-derived figures *"come off a
 *    screen that may be a cached snapshot, and are recomputed from the database at submission"*, and the
 *    server logs a correction when they moved. So the confirmation says the recorded figure is the
 *    server's, and never claims the screen's arithmetic is final.
 *
 * **`forDate` matters more than it looks.** Without it the server assumes **today**. A driver settling
 * yesterday's shift at 01:00 would hand over against the wrong day's orders — so this always sends the day
 * being settled, and the calculator can only be opened on a day, never on *Všetko*.
 */

export type Field =
  | 'card'
  | 'vouchers'
  | 'fuel'
  | 'purchase1'
  | 'purchase2'
  | 'payout1'
  | 'payout2'
  | 'cardCourier'

export const FIELDS: readonly { field: Field; label: string; note?: boolean }[] = [
  // Legacy's five plain rows and two with a note, in its order — a driver settling a shift is reading
  // down a list they know by heart. The card row is the one relabelled: it says where to read it from.
  { field: 'card', label: tt('Karta (podľa terminálu)') },
  // FB-068. The row that did not exist: card taken at the door had nowhere to go, so it was folded
  // into the row above or left out — and in 103 of the 363 recorded handovers the entered figure
  // disagrees with the register, invisibly, because the record still adds up either way.
  { field: 'cardCourier', label: tt('Karta u zákazníka') },
  { field: 'vouchers', label: tt('Lístky') },
  { field: 'fuel', label: tt('Tankovanie') },
  { field: 'purchase1', label: tt('Nákup 1') },
  { field: 'purchase2', label: tt('Nákup 2') },
  { field: 'payout1', label: tt('Výplata'), note: true },
  { field: 'payout2', label: tt('Výplata 2'), note: true },
]

export type Draft = Record<Field, string>
export type Notes = { payout1: string; payout2: string }

export const EMPTY_DRAFT: Draft = {
  card: '',
  cardCourier: '',
  vouchers: '',
  fuel: '',
  purchase1: '',
  purchase2: '',
  payout1: '',
  payout2: '',
}

/**
 * The terminal card figure the orders imply, offered as a starting point.
 *
 * **This used to be terminal plus courier, and the change is FB-068.** Both are card taken on a
 * machine and both settle to the restaurant, so the 363 recorded handovers typed them as one number —
 * of the 25 where both are non-zero, 17 deduct exactly their sum and none deduct just one. With no
 * field for the door there was nowhere else to put it.
 *
 * Now there is, so this must be the terminal alone: leaving the sum here and adding the door below
 * would deduct the courier figure **twice**, and every driver would hand over that much too little.
 * The two boxes together still start at the same total the one box did.
 */
export function suggestedCardCents(totals: DayTotals): number {
  return totals.terminalCents
}

/** Card taken at the door — its own deduction since FB-068, and its own box. */
export function suggestedDoorCents(totals: DayTotals): number {
  return totals.courierCents
}

export function prefill(totals: DayTotals): Draft {
  return {
    ...EMPTY_DRAFT,
    card: centsToInput(suggestedCardCents(totals)),
    cardCourier: centsToInput(suggestedDoorCents(totals)),
  }
}

function centsToInput(cents: number): string {
  if (cents === 0) return ''
  const whole = Math.trunc(cents / 100)
  const rest = String(Math.abs(cents % 100)).padStart(2, '0')
  return `${whole},${rest}`
}

/**
 * One typed amount, in cents — `null` when it is not a number at all.
 *
 * `parseMoneyToCents` answers 0 for anything it cannot read, which is right for rendering a legacy row
 * and wrong for a form: it would turn a typo into a silent zero and hand over the wrong money.
 */
export function typedCents(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return 0
  if (!/^-?\d+(?:[.,]\d{1,2})?$/.test(trimmed)) return null
  return parseMoneyToCents(trimmed)
}

/** Every typed field, in cents. A field nobody filled in is zero, not a reason to refuse the form. */
export function amountsOf(draft: Draft): Record<Field, number> {
  const amounts = {} as Record<Field, number>
  for (const { field } of FIELDS) amounts[field] = typedCents(draft[field]) ?? 0
  return amounts
}

export interface Handover {
  /** Everything counted for the day, whatever it was paid with. */
  revenueCents: number
  /** Online payments — they never reached the driver. */
  onlineCents: number
  /**
   * QR payments at the door - §9.741, owner O112. The customer paid the bank from the driver's QR code, so
   * the money never reached the driver either, and it comes off automatically beside the online figure.
   */
  qrPayCents: number
  /** The typed deductions, added up. */
  deductedCents: number
  /** What is left to hand over. */
  cashToHandOverCents: number
  /** The cash the orders say was collected, for the comparison below. */
  cashCollectedCents: number
}

export function computeHandover(totals: DayTotals, draft: Draft): Handover {
  const amounts = amountsOf(draft)
  // A plain integer reduce: these are already cents (FB-056).
  const deducted = FIELDS.reduce((total, { field }) => total + amounts[field], 0)
  return {
    revenueCents: totals.totalCents,
    onlineCents: totals.onlineCents,
    qrPayCents: totals.qrPayCents,
    deductedCents: deducted,
    cashToHandOverCents: totals.totalCents - totals.onlineCents - totals.qrPayCents - deducted,
    cashCollectedCents: totals.cashCents,
  }
}

/**
 * What comes off without the driver typing it - the sheet's *odrátajú sa automaticky* line, §9.741.
 *
 * **Both named, always** (§9.767, owner O129): it named QR Pay only on a day that took some, and read *Online
 * platby sa odrátajú automaticky* on every other, so a driver never saw that QR Pay comes off too.
 */
export function automaticNote(handover: Handover): string {
  // §9.767, owner O129 - both by name, always: a 0,00 € QR Pay is a day with no door QR payment, and the online
  // card is *CardPay online* here as on every screen.
  return `${tt('CardPay online a QR Pay sa odrátajú automaticky')}: ${cardPayOnlineLabel()} ${formatCents(handover.onlineCents)}, ${qrPayLabel()} ${formatCents(handover.qrPayCents)}`
}

/**
 * What is wrong with the form, in the order a driver would hit it.
 *
 * Nothing here is a house rule invented for the screen — each one is a shape the server would either
 * refuse (`@PositiveOrZero`) or accept and record as nonsense.
 */
export function problems(draft: Draft, day: string | null): string[] {
  const found: string[] = []
  if (day === null) {
    // Without a day the server assumes today, and a shift settled after midnight lands on the wrong one.
    found.push(tt('Vyberte deň, ktorý odovzdávate - inak by sa to zapísalo na dnešok.'))
  }
  for (const { field, label } of FIELDS) {
    const raw = draft[field].trim()
    if (raw === '') continue
    const parsed = typedCents(raw)
    if (parsed === null) found.push(`${tt('Suma')} „${label}“ ${tt('nie je číslo.')}`)
    // `@PositiveOrZero` on every one of them — the server would refuse it, so say so here.
    else if (parsed < 0) found.push(`${tt('Suma')} „${label}“ ${tt('nemôže byť záporná.')}`)
  }
  return found
}

/**
 * Who the server will accept a handover from.
 *
 * `POST /revenue-handovers` is `@RequireAuth({"driver", "waiter"})` while `GET /employees/my-wallet` also
 * admits `main_admin`, `admin` and `cook`. FB-001: admit whoever the server lets read, disable what it
 * will not let them write, say why. In this data an administrator's own wallet is empty anyway — every
 * one of the 2 813 orders with an `employee_id` belongs to a waiter — so the refusal is reached by a cook
 * or by an administrator who has personally taken an order, not by a hypothetical.
 */
export function maySubmit(systemRole: string | undefined, alsoDrives: boolean): boolean {
  // §9.686, owner #25 - and an account the main admin marked *Jazdí aj rozvoz*: the endpoint admits it since
  // §10A.344 and recomputes its figures over every order that names it. Required rather than optional, §9.480's rule.
  return systemRole === 'driver' || systemRole === 'waiter' || alsoDrives
}

export function refusalNote(): string {
  return tt(
    'Tržbu odovzdáva ten, kto ju vybral - server prijme odovzdanie len od šoféra alebo čašníka. Toto je pohľad, nie odovzdanie.',
  )
}

/**
 * Said when the result is negative.
 *
 * Not refused: `cashToHandOver` is `@NotNull` and nothing else, and a driver who really did spend more
 * than they took needs to record that, not fight a form. But it is the shape of a mistyped deduction, so
 * it is said out loud before the button rather than discovered by an administrator afterwards.
 */
export function shortfallNote(handover: Handover): string {
  if (handover.cashToHandOverCents >= 0) return ''
  return `${tt('Vyšlo mínus')} ${formatCents(Math.abs(handover.cashToHandOverCents))} - ${tt('to znamená, že ste minuli viac, než ste vybrali. Ak to sedí, pošlite to tak; ak nie, skontrolujte odrátané sumy.')}`
}

/**
 * Said when the typed card figure differs from what the orders imply.
 *
 * Both numbers are legitimate — the terminal is what the bank settles, the orders are what was rung up —
 * and the gap between them is the thing worth noticing, so it is stated rather than resolved.
 */
export function cardMismatchNote(totals: DayTotals, draft: Draft): string {
  const typed = typedCents(draft.card)
  if (typed === null || draft.card.trim() === '') return ''
  const implied = suggestedCardCents(totals)
  if (typed === implied) return ''
  const difference = typed - implied
  const direction = difference > 0 ? tt('viac') : tt('menej')
  return `${tt('Terminál hovorí o')} ${formatCents(Math.abs(difference))} ${direction} ${tt('než objednávky')} (${formatCents(implied)}). ${tt('Platí terminál - banka vyrovná jeho. Rozdiel býva platba mimo systému.')}`
}

/** Where the headline figure came from, in one line, because a result nobody can retrace is a guess. */
export function derivationNote(handover: Handover): string {
  // §9.767 - both terms always, by their names, so the working reads as the split above it does.
  return `${formatCents(handover.revenueCents)} ${tt('tržba')} − ${formatCents(handover.onlineCents)} ${cardPayOnlineLabel()} − ${formatCents(handover.qrPayCents)} ${qrPayLabel()} − ${formatCents(handover.deductedCents)} ${tt('odrátané')} = ${formatCents(handover.cashToHandOverCents)}`
}

/** Said after a successful submission, without claiming the screen's arithmetic was the last word. */
export function recordedNote(): string {
  return tt(
    'Zapísané. Server si sumy prepočíta z objednávok - ak sa medzitým niečo zmenilo, uloží sa jeho číslo a rozdiel sa zaznamená.',
  )
}

/** Cents back to the euros the endpoint takes, since its fields are `BigDecimal` and orval types them number. */
function euros(cents: number): number {
  return centsToEuros(cents)
}

/**
 * The body `POST /revenue-handovers` takes.
 *
 * Every order-derived figure is sent even though the server recomputes it: that is what lets the
 * reconciliation log say *what the screen thought* against *what was true*, which is the only record of a
 * stale snapshot there will ever be.
 */
export function handoverPayload(
  totals: DayTotals,
  draft: Draft,
  notes: Notes,
  day: string,
): Record<string, unknown> {
  const amounts = amountsOf(draft)
  const handover = computeHandover(totals, draft)
  return {
    totalRevenue: euros(handover.revenueCents),
    cardPayAmount: euros(totals.onlineCents),
    cardAmount: euros(amounts.card),
    // FB-068. Sent as its own figure so the audit compares the terminal against the terminal and the
    // door against the door, instead of inferring which convention the person used.
    cardCourierAmount: euros(amounts.cardCourier),
    vouchersAmount: euros(amounts.vouchers),
    fuelAmount: euros(amounts.fuel),
    purchase1Amount: euros(amounts.purchase1),
    purchase2Amount: euros(amounts.purchase2),
    payout1Amount: euros(amounts.payout1),
    payout1Note: notes.payout1.trim() === '' ? undefined : notes.payout1.trim(),
    payout2Amount: euros(amounts.payout2),
    payout2Note: notes.payout2.trim() === '' ? undefined : notes.payout2.trim(),
    cashToHandOver: euros(handover.cashToHandOverCents),
    orderCount: totals.count,
    cashTotal: euros(totals.cashCents),
    cardTerminalTotal: euros(totals.terminalCents),
    cardCourierTotal: euros(totals.courierCents),
    cardOnlineTotal: euros(totals.onlineCents),
    // §9.741, owner O112 - the figure `cashToHandOver` above already has off. Sent so a record the server
    // cannot recompute still says how much QR Pay it took off; an app that sends none is recorded as one
    // that predates QR Pay (§10A.390), whose cash never had it off.
    qrPayTotal: euros(totals.qrPayCents),
    // Always sent. Without it the server assumes today, and a shift settled at 01:00 is yesterday's.
    forDate: day,
  }
}
